/**
 * Promo Types - TypeScript Definitions for Promo Code Management
 */

// Promo Status enum
export enum PromoStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  PAUSED = 'PAUSED',
  ARCHIVED = 'ARCHIVED',
  EXPIRED = 'EXPIRED',
  INACTIVE = 'INACTIVE',
}

// Domain enum (renamed to PromoDomain)
export enum PromoDomain {
  ACADEMY = 'ACADEMY',
  SHOP = 'SHOP',
  BOOKING = 'BOOKING',
  COACHING = 'COACHING',
}

// Legacy Domain enum for backward compatibility
export enum Domain {
  SHOP = 'SHOP',
  ACADEMY = 'ACADEMY',
  VENUE = 'VENUE',
  BOOKING = 'BOOKING',
  COACHING = 'COACHING',
}

// Transaction Type enum
export enum TransactionType {
  ACADEMY_ENROLLMENT = 'ACADEMY_ENROLLMENT',
  SHOP_PURCHASE = 'SHOP_PURCHASE',
  VENUE_BOOKING = 'VENUE_BOOKING',
  COACHING_ENROLLMENT = 'COACHING_ENROLLMENT',
}

// User Type enum (Eligibility User Type)
export enum UserType {
  ALL = 'ALL',
  NEW = 'NEW',
  RENEWAL = 'RENEWAL',
  REPEAT = 'REPEAT',
}

// Benefit Type enum
export enum BenefitType {
  PERCENTAGE_DISCOUNT = 'PERCENTAGE_DISCOUNT',
  FIXED_DISCOUNT = 'FIXED_DISCOUNT',
  FLAT_DISCOUNT = 'FLAT_DISCOUNT', // Alias for FIXED_DISCOUNT
  FREE_SHIPPING = 'FREE_SHIPPING',
}

// Discount Mode enum
export enum DiscountMode {
  AUTOMATIC = 'AUTOMATIC',
  CODE_BASED = 'CODE_BASED',
}

// Promo Visibility Type enum
export enum PromoVisibilityType {
  PUBLIC = 'PUBLIC',
  ASSIGNED = 'ASSIGNED',
}

// Benefit Level enum
export enum BenefitLevel {
  ORDER = 'ORDER',
  ITEM = 'ITEM',
}

// Entity Targeting Mode enum
export enum EntityTargetingMode {
  INCLUDE = 'INCLUDE',
  EXCLUDE = 'EXCLUDE',
}

// Eligibility interface
export interface Eligibility {
  userType: UserType;
}

// Benefit interface
export interface Benefit {
  type: BenefitType;
  level: BenefitLevel;
  value: number; // Percentage or fixed amount
  maxDiscountAmount?: number; // For percentage discounts
  applicableCatalogueCategory?: string; // Category for item-level discounts
}

// Entity Targeting interface
export interface EntityTargeting {
  mode: EntityTargetingMode;
  entityIds?: string[];
  subEntityIds?: string[];
}

// Scope Filter interface
export interface ScopeFilter {
  field: string;
  operator: string;
  value: string[] | number[] | string | number;
}

// Scope interface
export interface Scope {
  entityTargeting?: EntityTargeting;
  filters?: ScopeFilter[];
}

// Usage Limits interface
export interface UsageLimits {
  totalLimit?: number;
  perUserLimit?: number;
}

// Stacking Rules interface
export interface StackingRules {
  allowWithReferral: boolean;
  allowWithOtherPromos: boolean;
  exclusive: boolean;
  maxTotalDiscountPercent?: number; // Max total discount percentage cap
}

// Conditions interface
export interface Conditions {
  minOrderAmount?: number;
  minQuantity?: number;
  minDurationMonths?: number;
  fullPaymentRequired?: boolean;
}

// Funding interface
export interface PromoFunding {
  fundedBy: 'PLATFORM' | 'SHARED';
  sharePercentage?: number; // 0-100
}

// Promo visibility interface
export interface PromoVisibility {
  type: PromoVisibilityType;
}

// Validity interface
export interface Validity {
  startAt: string; // ISO date string (YYYY-MM-DD)
  endAt: string; // ISO date string (YYYY-MM-DD)
  scheduleActivation?: boolean; // Schedule activation checkbox
  scheduleDate?: string; // ISO date string (YYYY-MM-DD) - Schedule activation date
}

// Base Promo interface
export interface Promo {
  id: string;
  name: string;
  code: string;
  listingTitle: string;
  appliedTitle: string;
  campaignId?: string;
  campaignName?: string;
  domain: PromoDomain;
  transactionType: TransactionType;
  status: PromoStatus;
  discountMode?: DiscountMode; // AUTOMATIC or CODE_BASED
  eligibility: Eligibility;
  benefit: Benefit;
  scope?: Scope;
  usageLimits?: UsageLimits;
  stackingRules?: StackingRules;
  conditions?: Conditions;
  funding?: PromoFunding;
  visibility?: PromoVisibility;
  validity: Validity;
  createdAt: string;
  updatedAt: string;
  
  // Metrics (from listing API)
  totalUsage?: number;
  perUserUsage?: number;
  revenueImpact?: number;
}

// Promo form data for creation/editing
export interface PromoFormData {
  name: string;
  code: string;
  listingTitle: string;
  appliedTitle: string;
  campaignId?: string;
  domain: PromoDomain;
  transactionType: TransactionType;
  discountMode?: DiscountMode;
  eligibility: Eligibility;
  benefit: Benefit;
  scope?: Scope;
  usageLimits?: UsageLimits;
  stackingRules?: StackingRules;
  conditions?: Conditions;
  funding?: PromoFunding;
  visibility?: PromoVisibility;
  validity: Validity;
}

// Promo step configuration
export interface PromoStepConfig {
  id: string;
  title: string;
  isOptional?: boolean;
  isCompleted: boolean;
}

// Promo list parameters
export interface PromoListParams {
  pageNum?: number;
  pageSize?: number;
  status?: PromoStatus;
  domain?: Domain;
  transactionType?: TransactionType;
  campaignId?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  startAt?: string; // Date string in YYYY-MM-DD format
  endAt?: string; // Date string in YYYY-MM-DD format
}

// Promo list response
export interface PromoListResponse {
  promos: Promo[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
}

// Promo validation errors
export interface PromoValidationErrors {
  name?: string;
  code?: string;
  listingTitle?: string;
  appliedTitle?: string;
  campaignId?: string;
  domain?: string;
  transactionType?: string;
  eligibility?: {
    userType?: string;
  };
  benefit?: {
    type?: string;
    level?: string;
    value?: string;
    maxDiscountAmount?: string;
    applicableCatalogueCategory?: string;
  };
  validity?: {
    startAt?: string;
    endAt?: string;
  };
  scope?: {
    filters?: string;
    entityTargeting?: string;
  };
  conditions?: {
    minOrderAmount?: string;
    minQuantity?: string;
    minDurationMonths?: string;
    fullPaymentRequired?: string;
  };
  usageLimits?: {
    totalLimit?: string;
    perUserLimit?: string;
  };
  stackingRules?: {
    maxTotalDiscountPercent?: string;
    allowWithReferral?: string;
    allowWithOtherPromos?: string;
    exclusive?: string;
  };
}
